/* LT-12.5 — Summary tab (page 1 of every tender).
 *
 * Layout:
 *   1) Left column — Things to Note   (rich text bullets, **bold** allowed)
 *   2) Right column — What's Excluded (plain-text bullets)
 *
 * Each section is a vertical list of authored items. Items support:
 *   • inline edit  (click to edit; blur or Enter to save)
 *   • add at end   (button below the list)
 *   • delete       (x icon on hover)
 *   • drag-reorder (vertical handle on hover)
 *
 * Persistence model — full-list PATCH on every change. Mirrors the
 * authoritative-list pattern used by `summary_*` backend events. Server
 * diffs the incoming array vs the stored one and emits granular audit
 * rows for added / edited / deleted / reordered.
 *
 * Rich text — Tiptap with the Bold extension only. Toolbar surfaces just
 * a "B" toggle to keep authoring focused.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold as BoldIcon, Edit3, FileText, GripVertical, Lock, Plus, Save, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card } from "../components/UI";

/* ============================================================
 * Markdown ↔ HTML helpers (single source of truth for **bold**)
 * ========================================================== */
function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function mdToHtml(md) {
  // Convert **bold** to <strong>bold</strong>. Other markdown intentionally
  // unsupported (spec locks bold-only). HTML-escape everything else.
  return escapeHtml(md).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}

function htmlToMd(html) {
  // Convert <strong> / <b> → **bold**. Strip all other tags.
  return String(html ?? "")
    .replace(/<\/?(strong|b)>/gi, "**")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .trim();
}

/* ============================================================
 * Rich-text editor (Tiptap, bold-only)
 * ========================================================== */
function RichBoldEditor({ value, onChange, onBlur, autoFocus, placeholder, testId }) {
  // value & onChange operate in markdown (the **bold** form).
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Keep ONLY bold. Disable everything else from StarterKit.
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        code: false,
        italic: false,
        strike: false,
      }),
    ],
    content: mdToHtml(value || ""),
    autofocus: autoFocus ? "end" : false,
    onUpdate({ editor: ed }) {
      onChange?.(htmlToMd(ed.getHTML()));
    },
    onBlur() {
      onBlur?.();
    },
    editorProps: {
      attributes: {
        class:
          "min-h-[2.25rem] outline-none px-2 py-1.5 rounded-md text-ink leading-snug " +
          "focus:bg-surface-2/70 transition-colors",
        "data-testid": testId,
      },
      handleKeyDown(_view, ev) {
        if (ev.key === "Enter" && !ev.shiftKey) {
          ev.preventDefault();
          editor?.commands.blur();
          return true;
        }
        return false;
      },
    },
  });

  // Keep editor in sync when external value changes (e.g. cancel reset).
  useEffect(() => {
    if (!editor) return;
    const current = htmlToMd(editor.getHTML());
    if ((value || "") !== current) {
      editor.commands.setContent(mdToHtml(value || ""), false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;
  return (
    <div className="relative group/editor">
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={
          "absolute -left-7 top-1.5 inline-flex items-center justify-center w-5 h-5 rounded " +
          "border border-line bg-surface text-ink-3 hover:text-ink hover:bg-surface-2 " +
          (editor.isActive("bold") ? "text-ink bg-surface-2" : "")
        }
        style={{ fontSize: 10 }}
        title={editor.isActive("bold") ? "Remove bold (Ctrl/Cmd+B)" : "Bold (Ctrl/Cmd+B)"}
        data-testid={`${testId || "ttn-editor"}-bold-btn`}
      >
        <BoldIcon size={11} />
      </button>
      <EditorContent
        editor={editor}
        placeholder={placeholder}
      />
    </div>
  );
}

/* ============================================================
 * Plain text input row (What's Excluded)
 * ========================================================== */
function PlainTextRow({ value, onChange, onBlur, autoFocus, testId, placeholder }) {
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange?.(e.target.value)}
      onBlur={onBlur}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      autoFocus={autoFocus}
      placeholder={placeholder || "Type and press Enter…"}
      className={
        "w-full min-h-[2.25rem] outline-none px-2 py-1.5 rounded-md " +
        "text-ink leading-snug bg-transparent focus:bg-surface-2/70 transition-colors"
      }
      data-testid={testId}
    />
  );
}

/* ============================================================
 * List section (Things to Note / What's Excluded)
 * ========================================================== */
function SummarySection({
  section,             // "things-to-note" | "whats-excluded"
  label,
  description,
  items,
  onPersist,           // (nextItems) => Promise
  saving,
  rich = false,
  bullet = "•",
  emptyHint,
}) {
  // local mirror so optimistic add/edit/delete/reorder feels instant
  const [local, setLocal] = useState(items);
  useEffect(() => { setLocal(items); }, [items]);

  // Drag state
  const dragIdRef = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const persistDebounced = useDebouncedPersist(onPersist);

  const addRow = () => {
    const id = `tmp-${Math.random().toString(36).slice(2, 10)}`;
    const next = [
      ...local,
      { id, body: "", display_order: local.length, source_library_id: null, __new: true },
    ];
    setLocal(next);
    // Don't persist yet — wait for blur (filtered out if still empty).
  };

  const updateRow = (id, body) => {
    setLocal((prev) => prev.map((r) => (r.id === id ? { ...r, body } : r)));
  };

  const blurRow = (id) => {
    // Drop empty rows; persist the cleaned list.
    const next = local
      .filter((r) => (r.body || "").trim() || !r.__new || r.id !== id)
      .map((r) => ({ ...r, __new: false }))
      .filter((r) => (r.body || "").trim());
    setLocal(next);
    persistDebounced(next);
  };

  const deleteRow = (id) => {
    const next = local.filter((r) => r.id !== id);
    setLocal(next);
    persistDebounced(next);
  };

  // Drag handlers
  const onDragStart = (e, id) => {
    dragIdRef.current = id;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    setDragOver(id);
  };
  const onDrop = (e, targetId) => {
    e.preventDefault();
    const sourceId = dragIdRef.current;
    dragIdRef.current = null;
    setDragOver(null);
    if (!sourceId || sourceId === targetId) return;
    const srcIdx = local.findIndex((r) => r.id === sourceId);
    const tgtIdx = local.findIndex((r) => r.id === targetId);
    if (srcIdx < 0 || tgtIdx < 0) return;
    const next = [...local];
    const [moved] = next.splice(srcIdx, 1);
    next.splice(tgtIdx, 0, moved);
    setLocal(next);
    persistDebounced(next);
  };

  return (
    <Card className="p-5" data-testid={`summary-${section}-card`}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3
            className="text-ink"
            style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.005em" }}
          >
            {label}
          </h3>
          {description ? (
            <p className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>
              {description}
            </p>
          ) : null}
        </div>
        {saving ? (
          <span
            className="text-ink-3 inline-flex items-center gap-1.5 font-mono tnum"
            style={{ fontSize: 10.5 }}
            data-testid={`summary-${section}-saving`}
          >
            <Save size={11} className="animate-pulse" /> Saving…
          </span>
        ) : null}
      </div>

      {local.length === 0 ? (
        <div
          className="rounded-md border border-dashed border-line text-ink-3 px-4 py-6 text-center"
          style={{ fontSize: 12 }}
          data-testid={`summary-${section}-empty`}
        >
          {emptyHint || "No items yet. Add the first one below."}
        </div>
      ) : (
        <ul className="space-y-1" data-testid={`summary-${section}-list`}>
          {local.map((row) => (
            <li
              key={row.id}
              draggable
              onDragStart={(e) => onDragStart(e, row.id)}
              onDragOver={(e) => onDragOver(e, row.id)}
              onDrop={(e) => onDrop(e, row.id)}
              onDragLeave={() => setDragOver(null)}
              className={
                "group flex items-start gap-1 -ml-1 pl-7 pr-1 rounded-md " +
                "border border-transparent hover:border-line " +
                (dragOver === row.id ? "bg-surface-2/70" : "")
              }
              data-testid={`summary-${section}-row-${row.id}`}
            >
              <span
                className="invisible group-hover:visible cursor-grab absolute -ml-6 mt-1.5 text-ink-4"
                title="Drag to reorder"
              >
                <GripVertical size={13} />
              </span>
              <span
                aria-hidden
                className="select-none mt-1.5"
                style={{ color: "#B97E2A", fontSize: 13, fontWeight: 700, width: 12, textAlign: "center" }}
              >
                {bullet}
              </span>
              <div className="flex-1">
                {rich ? (
                  <RichBoldEditor
                    value={row.body}
                    onChange={(v) => updateRow(row.id, v)}
                    onBlur={() => blurRow(row.id)}
                    autoFocus={!!row.__new}
                    placeholder="Add a note (Ctrl/Cmd+B for bold)…"
                    testId={`summary-${section}-input-${row.id}`}
                  />
                ) : (
                  <PlainTextRow
                    value={row.body}
                    onChange={(v) => updateRow(row.id, v)}
                    onBlur={() => blurRow(row.id)}
                    autoFocus={!!row.__new}
                    placeholder="Add an exclusion…"
                    testId={`summary-${section}-input-${row.id}`}
                  />
                )}
              </div>
              <button
                type="button"
                onClick={() => deleteRow(row.id)}
                className="opacity-0 group-hover:opacity-100 inline-flex items-center justify-center w-6 h-6 rounded text-ink-3 hover:text-red-600 hover:bg-red-50"
                title="Delete"
                data-testid={`summary-${section}-delete-${row.id}`}
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={addRow}
        className="mt-3 inline-flex items-center gap-1.5 text-ink-3 hover:text-ink rounded-md px-2 py-1.5 hover:bg-surface-2"
        style={{ fontSize: 12 }}
        data-testid={`summary-${section}-add-btn`}
      >
        <Plus size={12} /> Add another
      </button>
    </Card>
  );
}

function useDebouncedPersist(fn, delay = 350) {
  const timer = useRef(null);
  return useCallback(
    (...args) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => fn?.(...args), delay);
    },
    [fn, delay],
  );
}

/* ============================================================
 * Item 3 — Internal Notes (tender-level) section
 * Private working notes; never appear in any client PDF.
 * Uses info-slate palette per plan v2 Correction 4.
 * ========================================================== */
function InternalNotesSection({ tenderId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tenders/${tenderId}/internal-notes`);
      const grp = r.data.grouped || { tender: [] };
      setItems(grp.tender || []);
    } catch (e) {
      // Silent — endpoint may 404 in mid-migration; degrade gracefully.
    } finally {
      setLoading(false);
    }
  }, [tenderId]);
  useEffect(() => { reload(); }, [reload]);

  const add = async () => {
    const body = (draft || "").trim();
    if (!body) return;
    try {
      await api.post(`/tenders/${tenderId}/internal-notes`, {
        scope: "tender", target_id: null, body_md: body,
      });
      setDraft("");
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add note"); }
  };

  const saveEdit = async (id) => {
    const body = (editingText || "").trim();
    if (!body) return;
    try {
      await api.patch(`/tenders/${tenderId}/internal-notes/${id}`, { body_md: body });
      setEditingId(null); setEditingText("");
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this internal note?")) return;
    try {
      await api.delete(`/tenders/${tenderId}/internal-notes/${id}`);
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso); const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    } catch { return ""; }
  };
  const mdToHtml = (md) => {
    const esc = String(md || "").replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;").replaceAll(">", "&gt;");
    return esc.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  };

  return (
    <div
      className="mb-6 rounded-lg border border-info/40 bg-info-soft/40 p-4"
      data-testid="internal-notes-section"
    >
      <div className="flex items-center gap-2 mb-1 text-info-deep">
        <Lock size={13} />
        <h3 className="font-mono uppercase" style={{ fontSize: 11, letterSpacing: "0.08em" }}>
          Internal notes
        </h3>
        <span className="text-info-deep/70" style={{ fontSize: 10.5 }}>
          — never appear in client documents
        </span>
      </div>
      <p className="text-ink-3 mb-3" style={{ fontSize: 11.5 }}>
        Working reasoning, decisions, reminders. Only visible on the LUX Copy internal PDF and here.
      </p>

      {loading ? (
        <div className="text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div
          className="rounded-md border border-dashed border-info/40 text-ink-3 px-4 py-4 text-center"
          style={{ fontSize: 12 }}
          data-testid="internal-notes-empty"
        >
          No internal notes yet. Add the first one below.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="internal-notes-list">
          {items.map((n) => (
            <li
              key={n.id}
              className="bg-surface border border-line rounded-md px-3 py-2 group"
              data-testid={`internal-note-item-${n.id}`}
            >
              {editingId === n.id ? (
                <div className="space-y-2">
                  <textarea
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    rows={3}
                    className="w-full bg-bg border border-line rounded px-2 py-1.5 text-ink"
                    style={{ fontSize: 12 }}
                    data-testid={`internal-note-edit-textarea-${n.id}`}
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => { setEditingId(null); setEditingText(""); }}
                      className="text-ink-3 hover:text-ink"
                      style={{ fontSize: 11.5 }}
                    >Cancel</button>
                    <button
                      onClick={() => saveEdit(n.id)}
                      className="inline-flex items-center gap-1 bg-info text-white rounded px-2 py-1"
                      style={{ fontSize: 11.5 }}
                      data-testid={`internal-note-save-${n.id}`}
                    ><Save size={11} /> Save</button>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className="text-ink"
                    style={{ fontSize: 12.5, lineHeight: 1.5 }}
                    dangerouslySetInnerHTML={{ __html: mdToHtml(n.body_md) }}
                  />
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-ink-3" style={{ fontSize: 10.5 }}>
                      {(n.updated_by_name || n.created_by_name || "—")} · {fmtDate(n.updated_at || n.created_at)}
                    </span>
                    <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100">
                      <button
                        onClick={() => { setEditingId(n.id); setEditingText(n.body_md); }}
                        className="inline-flex items-center gap-1 text-ink-3 hover:text-info"
                        style={{ fontSize: 11 }}
                        data-testid={`internal-note-edit-btn-${n.id}`}
                      ><Edit3 size={11} /> Edit</button>
                      <button
                        onClick={() => del(n.id)}
                        className="inline-flex items-center gap-1 text-ink-3 hover:text-red-700"
                        style={{ fontSize: 11 }}
                        data-testid={`internal-note-delete-btn-${n.id}`}
                      ><Trash2 size={11} /> Delete</button>
                    </div>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder="Add an internal note (bold with **markdown**)."
          className="w-full bg-surface border border-line rounded px-2 py-1.5 text-ink"
          style={{ fontSize: 12 }}
          data-testid="internal-note-add-textarea"
        />
        <div className="mt-2 flex justify-end">
          <button
            onClick={add}
            disabled={!draft.trim()}
            className="inline-flex items-center gap-1 bg-info text-white rounded px-3 py-1.5 disabled:opacity-40"
            style={{ fontSize: 11.5 }}
            data-testid="internal-note-add-btn"
          ><Plus size={11} /> Add note</button>
        </div>
      </div>
    </div>
  );
}


/* ============================================================
 * Top-level Summary tab
 * ========================================================== */
export default function TenderSummary({ tender, onTenderUpdate }) {
  const [data, setData] = useState({
    things_to_note: tender.things_to_note || [],
    whats_excluded: tender.whats_excluded || [],
  });
  const [savingSection, setSavingSection] = useState(null);

  // Re-fetch on mount to ensure we have the latest (e.g. if user navigated
  // back after a backfill / template re-run).
  useEffect(() => {
    let alive = true;
    api.get(`/tenders/${tender.id}/summary`).then((r) => {
      if (!alive) return;
      setData({
        things_to_note: r.data.things_to_note || [],
        whats_excluded: r.data.whats_excluded || [],
      });
    }).catch(() => {/* keep optimistic */});
    return () => { alive = false; };
  }, [tender.id]);

  const persistSection = async (section, nextItems) => {
    const path = section === "things-to-note"
      ? `/tenders/${tender.id}/summary/things-to-note`
      : `/tenders/${tender.id}/summary/whats-excluded`;
    setSavingSection(section);
    try {
      // Strip optimistic-only fields before sending. Server assigns ids
      // for new rows; we forward stable ids for existing rows.
      const payload = {
        items: nextItems.map((r, idx) => ({
          // Drop tmp- ids so the server treats them as new (assigns UUID).
          ...(r.id && !r.id.startsWith("tmp-") ? { id: r.id } : {}),
          body: r.body,
          display_order: idx,
          source_library_id: r.source_library_id || null,
        })),
      };
      const { data: resp } = await api.patch(path, payload);
      const stateKey = section === "things-to-note" ? "things_to_note" : "whats_excluded";
      setData((d) => ({ ...d, [stateKey]: resp[stateKey] || [] }));
      // Bubble up so the parent has the latest tender content (in case
      // the audit drawer / export uses it).
      onTenderUpdate?.({
        ...tender,
        [stateKey]: resp[stateKey] || [],
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSavingSection(null);
    }
  };

  return (
    <div className="mt-6" data-testid="tender-notes-tab">
      {/* Item 3 — Private working notes. Never appear in any client PDF. */}
      <InternalNotesSection tenderId={tender.id} />

      {/* External — client-facing. Unchanged behaviour. */}
      <div className="mb-2 flex items-center gap-2 text-ink-2">
        <FileText size={13} />
        <h3 className="font-mono uppercase" style={{ fontSize: 11, letterSpacing: "0.08em" }}>
          Client-facing notes
        </h3>
        <span className="text-ink-3" style={{ fontSize: 10.5 }}>
          — appear on the Summary cover of every client PDF
        </span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SummarySection
          section="things-to-note"
          label="Things to Note"
          description="Short notes for the client. **Bold** allowed."
          items={data.things_to_note}
          rich
          bullet="·"
          emptyHint="No notes yet. Add the first one — these appear on the Summary cover of every PDF."
          saving={savingSection === "things-to-note"}
          onPersist={(next) => persistSection("things-to-note", next)}
        />
        <SummarySection
          section="whats-excluded"
          label="What's Excluded"
          description="Plain statements only. One line each."
          items={data.whats_excluded}
          bullet="—"
          emptyHint="No exclusions yet. Add the first one — these appear on the Summary cover of every PDF."
          saving={savingSection === "whats-excluded"}
          onPersist={(next) => persistSection("whats-excluded", next)}
        />
      </div>
    </div>
  );
}
